import React from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { TextField, Toolbar } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import Box from "@material-ui/core/Box";
import Autocomplete from "@material-ui/lab/Autocomplete";
import addPlayerStyles from "../../common/styles/sports/AddPlayer.styles";
import commonStyles from "../../common/styles/Common.styles";
import { usePlayersContext } from "../../../state/players/context";
import { useSportContext } from "../../../state/sport/context";

const AddPlayer = ({ team, player, onSave, onCancel }) => {
	const commonClasses = commonStyles();
	const classes = addPlayerStyles();

	const { state, savePlayer } = usePlayersContext();
	const players = state.players(team.id);
	let playerUniforms = players.map((p) => p.uniform);
	if (player) {
		playerUniforms = playerUniforms.filter((pu) => pu !== player?.uniform);
	}

	const { state: sportState } = useSportContext();
	const years = sportState.allowedYears();
	const uniformLength = 3;

	const validationSchema = Yup.object({
		firstName: Yup.string().required("First name is required"),
		lastName: Yup.string().required("Last name is required"),
		nationalRanking: Yup.string()
			.nullable()
			.matches(/^[0-9]{0,4}$/, "Must be 4 numeric characters or less"),
		regionalRanking: Yup.string()
			.nullable()
			.matches(/^[0-9]{0,4}$/, "Must be 4 numeric characters or less"),
		conferenceRanking: Yup.string()
			.nullable()
			.matches(/^[0-9]{0,4}$/, "Must be 4 numeric characters or less"),
		year: Yup.string().nullable(),
		uniform: Yup.string()
			.matches(
				/^[A-Za-z0-9]{1,3}$/,
				`Number must contain up to ${uniformLength} alpha characters`
			)
			.notOneOf(playerUniforms, "Number already exists")
			.nullable()
	});

	const formik = useFormik({
		initialValues: {
			firstName: "",
			lastName: "",
			uniform: "",
			nationalRanking: null,
			regionalRanking: null,
			conferenceRanking: null,
			...player
		},
		validationSchema,
		onSubmit: (values) => {
			savePlayer({
				...values,
				...{ teamId: team.id }
			});

			if (onSave) {
				onSave();
			}
		}
	});

	const {
		errors,
		touched,
		handleSubmit,
		handleChange,
		handleBlur,
		isValid,
		dirty,
		setFieldValue
	} = formik;

	const setYear = (year) => {
		setFieldValue("year", year);
	};

	const cancel = () => {
		if (onCancel) {
			onCancel();
		}
	};

	return (
		<Box className={commonClasses.container}>
			<Box className={classes.contentContainer}>
				<form className={commonClasses.form} onSubmit={handleSubmit}>
					<Grid container alignItems="center" className={classes.detailsContainer}>
						<Grid
							container
							spacing={3}
							direction="column"
							item
							xs={12}
							sm={12}
							md={4}
						>
							<Grid item>
								<TextField
									id="uniform"
									name="uniform"
									label="Number"
									fullWidth
									helperText={touched.uniform ? errors.uniform : ""}
									error={touched.uniform && Boolean(errors.uniform)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.uniform}
								/>
							</Grid>
							<Grid item>
								<TextField
									id="firstName"
									name="firstName"
									label="First name"
									fullWidth
									helperText={touched.firstName ? errors.firstName : ""}
									error={touched.firstName && Boolean(errors.firstName)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.firstName}
								/>
							</Grid>
							<Grid item>
								<TextField
									id="lastName"
									name="lastName"
									label="Last name"
									fullWidth
									helperText={touched.lastName ? errors.lastName : ""}
									error={touched.lastName && Boolean(errors.lastName)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.lastName}
								/>
							</Grid>
							<Grid item>
								<Autocomplete
									id="year"
									options={years.map((y) => y.name)}
									getOptionLabel={(name) => {
										const option = years.find((y) => y.name === name);
										return option ? `${option.name} (${option.code})` : "";
									}}
									renderInput={(params) => (
										<TextField {...params} label="Year" variant="outlined" />
									)}
									onChange={(event, value) => {
										setYear(value);
									}}
									defaultValue={formik.values.year}
								/>
							</Grid>
							<Grid item>
								<TextField
									id="national-ranking"
									name="nationalRanking"
									label="National Ranking"
									fullWidth
									helperText={touched.nationalRanking ? errors.nationalRanking : ""}
									error={touched.nationalRanking && Boolean(errors.nationalRanking)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.nationalRanking}
								/>
							</Grid>
							<Grid item>
								<TextField
									id="regional-ranking"
									name="regionalRanking"
									label="Regional Ranking"
									fullWidth
									helperText={touched.regionalRanking ? errors.regionalRanking : ""}
									error={touched.regionalRanking && Boolean(errors.regionalRanking)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.regionalRanking}
								/>
							</Grid>
							<Grid item>
								<TextField
									id="conference-ranking"
									name="conferenceRanking"
									label="Conference Ranking"
									fullWidth
									helperText={touched.conferenceRanking ? errors.conferenceRanking : ""}
									error={touched.conferenceRanking && Boolean(errors.conferenceRanking)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.conferenceRanking}
								/>
							</Grid>
						</Grid>
					</Grid>

					<Toolbar className={commonClasses.actionToolbar}>
						<Grid container direction="row" justifyContent="flex-end" spacing={2}>
							<Grid item>
								<SecondaryButton color="secondary" label="Cancel" onClick={cancel} />
							</Grid>
							<Grid item>
								<PrimaryButton
									type="submit"
									color="primary"
									label="Save"
									disabled={!(isValid && dirty)}
								/>
							</Grid>
						</Grid>
					</Toolbar>
				</form>
			</Box>
		</Box>
	);
};

export default AddPlayer;
